import React, { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import '../../styles/ImagePickerModal.css';

const FIELD_TABS = [
  { key: 'vertical', label: 'Poster', column: 'vertical_image' },
  { key: 'horizontal', label: 'Thumbnail', column: 'horizontal_image' },
];

const SOURCE_TABS = [
  { key: 'recommended', label: 'Recommended' },
  { key: 'all', label: 'All' },
  { key: 'custom', label: 'Custom' },
];

// Modal for picking a poster/thumbnail image for a media item: switch between the poster and
// thumbnail slot, then choose an image from this item's own seasons ("Recommended"), search
// across every image in the library ("All"), or upload/reuse a custom image ("Custom").
const ImagePickerModal = ({ isOpen, onClose, mediaItem, apiBaseUrl, onImageSelected, initialField = 'vertical' }) => {
  const [activeField, setActiveField] = useState(initialField);
  const [activeTab, setActiveTab] = useState('recommended');
  const [searchQuery, setSearchQuery] = useState('');
  const [images, setImages] = useState([]);
  const [currentPath, setCurrentPath] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const fieldColumn = FIELD_TABS.find((f) => f.key === activeField)?.column || 'vertical_image';

  useEffect(() => {
    if (isOpen) {
      setActiveField(initialField);
      setActiveTab('recommended');
      setSearchQuery('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialField, mediaItem?.id]);

  const fetchImages = useMemo(
    () =>
      debounce((field, tab, query) => {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams({ type: field, tab });
        if (query) params.set('q', query);
        fetch(`${apiBaseUrl}/media/${mediaItem.id}/image_options?${params.toString()}`, {
          credentials: 'include',
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.error) throw new Error(data.error);
            setImages(data.images || []);
            setCurrentPath(data.current || null);
          })
          .catch((err) => setError(err.message || 'Failed to load images'))
          .finally(() => setLoading(false));
      }, 300),
    [apiBaseUrl, mediaItem?.id]
  );

  useEffect(() => {
    if (!isOpen || !mediaItem?.id) return;
    fetchImages(activeField, activeTab, searchQuery);
  }, [isOpen, mediaItem?.id, activeField, activeTab, searchQuery, fetchImages]);

  if (!isOpen) return null;

  const selectImage = (path) => {
    setCurrentPath(path);
    fetch(`${apiBaseUrl}/media/${mediaItem.id}/select_image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ type: activeField, path }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          onImageSelected?.(fieldColumn, data.media);
        } else {
          setError(data.error || 'Failed to set image');
        }
      })
      .catch((err) => setError(err.message || 'Failed to set image'));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('image_type', activeField);
    setUploading(true);
    setError(null);
    fetch(`${apiBaseUrl}/media/${mediaItem.id}/upload_custom_image`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setImages((prev) => [data.image, ...prev]);
          selectImage(data.image.path);
        } else {
          setError(data.error || 'Failed to upload image');
        }
      })
      .catch((err) => setError(err.message || 'Failed to upload image'))
      .finally(() => setUploading(false));
  };

  const previewUrl = mediaItem?.[fieldColumn];

  return (
    <div className="image-picker-overlay" onClick={onClose}>
      <div className="image-picker-content" onClick={(e) => e.stopPropagation()}>
        <div className="image-picker-header">
          <div className="image-picker-field-tabs">
            {FIELD_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`image-picker-field-tab ${activeField === tab.key ? 'active' : ''}`}
                onClick={() => setActiveField(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <button className="image-picker-close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        <div className={`image-picker-preview ${activeField}`}>
          {previewUrl ? (
            <img src={previewUrl} alt="Current" />
          ) : (
            <div className="image-picker-preview-placeholder">No image selected</div>
          )}
        </div>

        <div className="image-picker-source-tabs">
          {SOURCE_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`image-picker-source-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'all' && (
          <input
            type="text"
            className="image-picker-search"
            placeholder="Search by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        )}

        {error && <div className="image-picker-error">{error}</div>}

        <div className="image-picker-scroll">
          {activeTab === 'custom' && (
            <button className="image-picker-upload-tile" onClick={handleUploadClick} disabled={uploading}>
              <span className="image-picker-upload-icon">+</span>
              <span>{uploading ? 'Uploading...' : 'Upload new'}</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          {loading ? (
            <div className="image-picker-status">Loading...</div>
          ) : images.length === 0 ? (
            activeTab !== 'custom' && <div className="image-picker-status">No images found</div>
          ) : (
            images.map((image) => (
              <button
                key={image.id}
                className={`image-picker-tile ${currentPath === image.path ? 'selected' : ''}`}
                onClick={() => selectImage(image.path)}
                title={image.label}
              >
                <img src={image.url} alt={image.label} />
                {currentPath === image.path && (
                  <span className="image-picker-checkmark">&#10003;</span>
                )}
                <span className="image-picker-tile-label">{image.label}</span>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePickerModal;
